import { useState, useEffect, useRef } from 'react';
import { useLoaderData, LoaderFunctionArgs } from 'react-router';
import { clubApi } from '@repo/api-client';
import { Input } from '@repo/ui/components/form/input';
import { CourtTimeSelector } from '@/components/tournaments/court-time-selector';
import { format } from 'date-fns';
import { Circle, CircleCheck, Users, Trophy } from 'lucide-react';

interface TimeSlot {
  id: string;
  reservationId?: string; // ID de la reserva en la base de datos
  courtId: string;
  date: string;
  startTime: string;
  endTime: string;
}

function ConfigurePhases() {
  const { tournament, availability, clubId } = useLoaderData<typeof loader>();
  const isInscriptionOnly = tournament.mode === 'INSCRIPTION_ONLY';

  const [groupPhaseSlots, setGroupPhaseSlots] = useState<TimeSlot[]>([]);
  const [eliminationDate, setEliminationDate] = useState<string>('');
  const [eliminationTime, setEliminationTime] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const eliminationTimeSaveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const tournamentStartDate = new Date(tournament.startDate);
  const tournamentEndDate = new Date(tournament.endDate);
  const lastDayExcluded = new Date(availability.tournamentDateRange.lastDayExcluded);

  // Set default elimination date to last day
  useEffect(() => {
    if (!isInscriptionOnly && !eliminationDate) {
      setEliminationDate(format(tournamentEndDate, 'yyyy-MM-dd'));
    }
  }, [eliminationDate, isInscriptionOnly, tournamentEndDate]);

  // Load existing slots from reservations (only GROUP_PHASE reservations for this tournament)
  useEffect(() => {
    const slots: TimeSlot[] = [];
    availability.courts.forEach(({ court, reservations }) => {
      reservations.forEach((reservation) => {
        // Only show GROUP_PHASE reservations that belong to this tournament
        // Other reservations are used for conflict checking but not displayed
        if (reservation.phase === 'GROUP_PHASE' && reservation.tournamentId === tournament.id) {
          const startDate = new Date(reservation.start);
          const endDate = new Date(reservation.end);
          slots.push({
            id: `${court.id}-${format(startDate, 'yyyy-MM-dd')}-${format(startDate, 'HH:mm')}-${
              reservation.id
            }`,
            reservationId: reservation.id,
            courtId: court.id,
            date: format(startDate, 'yyyy-MM-dd'),
            startTime: format(startDate, 'HH:mm'),
            endTime: format(endDate, 'HH:mm'),
          });
        }
      });
    });
    setGroupPhaseSlots(slots);
  }, [availability, tournament.id]);

  // Set elimination phase from tournament data if available
  useEffect(() => {
    if (isInscriptionOnly) {
      return;
    }

    const tournamentWithPhases = tournament as any;
    if (
      tournamentWithPhases.eliminationPhaseStartDate &&
      tournamentWithPhases.eliminationPhaseStartTime
    ) {
      const eliminationDateObj = new Date(tournamentWithPhases.eliminationPhaseStartDate);
      setEliminationDate(format(eliminationDateObj, 'yyyy-MM-dd'));
      setEliminationTime(tournamentWithPhases.eliminationPhaseStartTime);
    }
  }, [isInscriptionOnly, tournament]);

  // Generate 24 hours for elimination phase start time (00:00 to 23:00)
  const generateHours = () => {
    return Array.from({ length: 24 }, (_, i) => `${String(i).padStart(2, '0')}:00`);
  };

  // Auto-save elimination phase when date or time changes
  useEffect(() => {
    if (isInscriptionOnly) {
      return;
    }

    if (!eliminationDate || !eliminationTime) {
      return;
    }

    // Clear existing timeout
    if (eliminationTimeSaveTimeoutRef.current) {
      clearTimeout(eliminationTimeSaveTimeoutRef.current);
    }

    // Set new timeout for debounced save
    eliminationTimeSaveTimeoutRef.current = setTimeout(async () => {
      setIsSaving(true);
      setError(null);
      try {
        await clubApi.updateEliminationPhase(clubId, tournament.id, {
          startDate: new Date(`${eliminationDate}T00:00:00`),
          startTime: eliminationTime,
        });
      } catch (err: any) {
        setError(err?.response?.data?.message || 'Error al guardar la fase de eliminación');
        console.error('Error saving elimination phase:', err);
      } finally {
        setIsSaving(false);
      }
    }, 1000); // 1 second debounce

    return () => {
      if (eliminationTimeSaveTimeoutRef.current) {
        clearTimeout(eliminationTimeSaveTimeoutRef.current);
      }
    };
  }, [eliminationDate, eliminationTime, clubId, isInscriptionOnly, tournament.id]);

  const handleEliminationTimeChange = (hour: string) => {
    setEliminationTime(hour);
  };

  const maxEliminationDate = format(tournamentEndDate, 'yyyy-MM-dd');
  const minEliminationDate = format(tournamentStartDate, 'yyyy-MM-dd');

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-medium">
          {isInscriptionOnly ? 'Horarios de inscripción' : 'Fases del Torneo'}
        </h2>
        <h3 className="text-sm text-neutral-500">
          {isInscriptionOnly
            ? 'Configura las canchas y horarios disponibles para que las parejas puedan anotarse según su disponibilidad.'
            : 'Configura las canchas y horarios para la fase de grupos y la fase de eliminación. Después podrás abrir las inscripciones.'}
        </h3>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-md text-red-800">{error}</div>
      )}

      {isSaving && (
        <div className="p-2 bg-blue-50 border border-blue-200 rounded-md text-blue-800 text-sm">
          Guardando cambios...
        </div>
      )}

      <hr className="my-4 border-neutral-200" />
      <div>
        {/* Group Phase Section */}
        <div className="space-y-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-100 rounded-lg shrink-0">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-md font-medium">Fase de Grupos</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {isInscriptionOnly
                  ? 'Selecciona las canchas y horarios disponibles para las inscripciones. No se puede seleccionar el último día del torneo.'
                  : 'Selecciona las canchas y horarios disponibles para la fase de grupos. No se puede seleccionar el último día del torneo.'}
              </p>
            </div>
          </div>

          <CourtTimeSelector
            courts={availability.courts}
            tournamentStartDate={tournamentStartDate}
            lastDayExcluded={lastDayExcluded}
            selectedSlots={groupPhaseSlots}
            onSlotsChange={async (slots) => {
              setGroupPhaseSlots(slots);
            }}
            onAddSlot={async (slot) => {
              setIsSaving(true);
              setError(null);
              try {
                const response = await clubApi.addGroupPhaseSlot(clubId, tournament.id, {
                  courtId: slot.courtId,
                  start: new Date(`${slot.date}T${slot.startTime}`),
                  end: new Date(`${slot.date}T${slot.endTime}`),
                });
                // Update slot with reservation ID
                setGroupPhaseSlots((prev) =>
                  prev.map((s) =>
                    s.id === slot.id ? { ...s, reservationId: response.reservation.id } : s
                  )
                );
              } catch (err: any) {
                setError(err?.response?.data?.message || 'Error al agregar la ventana de tiempo');
                // Remove the slot if it failed to save
                setGroupPhaseSlots((prev) => prev.filter((s) => s.id !== slot.id));
              } finally {
                setIsSaving(false);
              }
            }}
            onRemoveSlot={async (slotId) => {
              const slot = groupPhaseSlots.find((s) => s.id === slotId);
              if (!slot?.reservationId) {
                // If no reservation ID, just remove from local state
                setGroupPhaseSlots((prev) => prev.filter((s) => s.id !== slotId));
                return;
              }

              setIsSaving(true);
              setError(null);
              try {
                await clubApi.removeGroupPhaseSlot(clubId, tournament.id, slot.reservationId);
                setGroupPhaseSlots((prev) => prev.filter((s) => s.id !== slotId));
              } catch (err: any) {
                setError(err?.response?.data?.message || 'Error al eliminar la ventana de tiempo');
              } finally {
                setIsSaving(false);
              }
            }}
          />

          {/* Capacity estimate */}
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-3">
              <div className="text-3xl font-bold text-blue-700">{groupPhaseSlots.length * 3}</div>
              <div className="text-sm text-blue-800">
                <p className="font-medium">Parejas máximo estimado</p>
              </div>
            </div>
          </div>
        </div>

        {!isInscriptionOnly && (
          <>
            <hr className="my-8 border-neutral-200" />

            {/* Elimination Phase Section */}
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="p-2 bg-amber-100 rounded-lg shrink-0">
                  <Trophy className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h3 className="text-md font-medium">Fase de Eliminación</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    Selecciona el día y la hora de inicio para la fase de eliminación. Los partidos
                    se encadenarán a partir de esta hora. La fase debe comenzar desde el último día
                    del torneo o días anteriores.
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <Input
                  type="date"
                  label="Día de Inicio"
                  value={eliminationDate}
                  onChange={(e) => setEliminationDate(e.target.value)}
                  min={minEliminationDate}
                  max={maxEliminationDate}
                  required
                />
                <div>
                  <label className="text-sm font-medium mb-2 block">Hora de Inicio</label>
                  <p className="text-sm text-muted-foreground mb-3">
                    Selecciona la hora de inicio para la fase de eliminación.
                  </p>
                  <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                    {generateHours().map((hour) => {
                      const isSelected = eliminationTime === hour;
                      return (
                        <button
                          key={hour}
                          type="button"
                          className={`flex items-center justify-center gap-2 rounded-lg border p-2 text-sm transition-colors ${
                            isSelected
                              ? 'border-neutral-300 bg-neutral-50'
                              : 'border-neutral-100 bg-white hover:bg-neutral-200'
                          } cursor-pointer`}
                          onClick={() => handleEliminationTimeChange(hour)}
                        >
                          {isSelected ? (
                            <CircleCheck className="h-3 w-3 stroke-primary stroke-2" />
                          ) : (
                            <Circle className="h-3 w-3 stroke-1" />
                          )}
                          <span>{hour}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              <div className="p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-800">
                <p>
                  <strong>Nota:</strong> Los partidos de eliminación se encadenarán automáticamente
                  a partir de la hora de inicio seleccionada. No es necesario especificar una hora
                  de fin.
                </p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

const loader = async ({ params }: LoaderFunctionArgs) => {
  const clubId = params.id!;
  const tournamentId = params.tournamentId!;
  const tournament = await clubApi.getTournamentById(clubId, tournamentId);
  const availability = await clubApi.getTournamentAvailability(clubId, tournamentId);

  return {
    tournament,
    availability,
    clubId,
  };
};

export const route = {
  path: 'configuring-datetime-phases',
  element: <ConfigurePhases />,
  loader,
};
